import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Dialog, DialogContent, DialogTitle} from "@/components/ui/dialog";
import {useBlockingGate} from "@/src/modules/core/hooks/use-blocking-gate";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {useToast} from "@/src/modules/core/hooks";
import {useStoredMe} from "@/src/modules/core/store/me";

import {PanicFalseAlarmDialog} from "./panic-false-alarm-dialog";
import {readPanicAlertId, subscribePanicEvents} from "../lib/panic-realtime-events";
import {getPanicErrorTitle} from "../lib/panic-alert-error";
import {usePanicAlert} from "../hooks/use-panic-alert";
import {usePanicAlertUpdate} from "../hooks/use-panic-alert-update";

export function PanicIncomingAlert() {
  const {t} = useTranslation("panic");
  const me = useStoredMe();
  const toast = useToast();
  const [activeAlertId, setActiveAlertId] = useState<number | null>(null);
  const [falseAlarmOpen, setFalseAlarmOpen] = useState(false);
  const {panicAlert, refetch, isFetching} = usePanicAlert(activeAlertId);
  const {mutateAsync: updateAlert, isPending: isUpdating} = usePanicAlertUpdate();
  const present = activeAlertId != null && !isViewingAsUser(me);
  const {isVisible} = useBlockingGate({id: "panic-alert", present});

  useEffect(() => {
    return subscribePanicEvents((event) => {
      const id = readPanicAlertId((event as CustomEvent<unknown>).detail);
      if (id != null) {
        setActiveAlertId(id);
      }
    });
  }, []);

  useEffect(() => {
    if (activeAlertId != null) {
      void refetch();
    }
  }, [activeAlertId, refetch]);

  if (!activeAlertId || !isVisible || isViewingAsUser(me)) {
    return null;
  }

  if (panicAlert?.initiated_by_user_id === me?.user_id) {
    return null;
  }

  const runAction = async (
    payload:
      | {is_acknowledged: true}
      | {is_resolved: true}
      | {is_false_alarm: true; false_alarm_reason: string},
  ) => {
    try {
      await updateAlert({id: activeAlertId, payload});
      toast.success(t("toast.updated"));
      setActiveAlertId(null);
    } catch (error) {
      toast.error(getPanicErrorTitle(error));
    }
  };

  return (
    <>
      <Dialog open={isVisible} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogTitle>PANIC ALERT</DialogTitle>
          <div className="flex flex-col gap-3 text-sm">
            <p>
              {panicAlert?.description?.trim() || "Incoming panic alert requires immediate response."}
            </p>
            <p className="text-muted-foreground">
              Property: {panicAlert?.property?.name ?? panicAlert?.property_id ?? "Unknown"}
            </p>
            <div className="flex flex-col gap-2">
              {panicAlert?.notified_recipients_snapshot?.map((recipient) => (
                <p key={`${recipient.recipient_type}-${recipient.label}`} className="text-xs text-muted-foreground">
                  {recipient.label ?? recipient.phone_number ?? "Recipient"}
                </p>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Button
                type="button"
                variant="outline"
                disabled={isUpdating || isFetching}
                onClick={() => {
                  void runAction({is_acknowledged: true});
                }}
              >
                Acknowledge
              </Button>
              <Button
                type="button"
                disabled={isUpdating || isFetching}
                onClick={() => {
                  void runAction({is_resolved: true});
                }}
              >
                Resolve
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isUpdating || isFetching}
                onClick={() => setFalseAlarmOpen(true)}
              >
                False alarm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PanicFalseAlarmDialog
        open={falseAlarmOpen}
        loading={isUpdating}
        onClose={() => setFalseAlarmOpen(false)}
        onConfirm={async (reason) => {
          await runAction({is_false_alarm: true, false_alarm_reason: reason});
          setFalseAlarmOpen(false);
        }}
      />
    </>
  );
}
